import React, { ComponentProps } from "react";
import Markdown, {
  ASTNode,
  MarkdownIt,
  RenderRules,
} from "react-native-markdown-display";
import { styled, Text, View, XStack } from "tamagui";

import { CheckDone, Delete, Warning } from "../../icons";
import {
  customeBackquotes,
  ExtendedASTNode,
} from "../../markdown-it/custom-block-quotes";
import { customeEmphasis } from "../../markdown-it/custom-bold";

function collectContent(node: ASTNode) {
  let result: string[] = [];

  if (node.content) {
    result.push(node.content);
  }

  if (Array.isArray(node.children)) {
    for (const child of node.children) {
      result = result.concat(collectContent(child));
    }
  }

  return result;
}

const IDENTIFIERS = [
  "success",
  "danger",
  "warning",
  "critical",
  "gray",
  "info",
] as const;

export type MdIdentifier = (typeof IDENTIFIERS)[number];

const markdownInst = MarkdownIt({ typographer: true })
  .use((md) => customeBackquotes(md, IDENTIFIERS))
  .use(customeEmphasis);

const BlockQuoteContainer = styled(View, {
  name: "BlockQuoteContainer",
  borderLeftWidth: "$0.5",
  paddingHorizontal: "$3",
  marginBottom: "$2",
  minHeight: "$5.3",
  variants: {
    variant: {
      success: {
        borderLeftColor: "$score-8",
        backgroundColor: "$lightgreen-100",
      },
      warning: {
        borderLeftColor: "$warning-500",
        backgroundColor: "$warning-100",
      },

      danger: {
        borderLeftColor: "$darkpink-500",
        backgroundColor: "$lightpink-100",
      },
      critical: {
        borderLeftColor: "$darkpink-500",
        backgroundColor: "$lightpink-100",
      },
      info: {
        borderLeftColor: "$score-10-9",
        backgroundColor: "$gray-50",
      },
      gray: {
        borderWidth: 0,
        paddingHorizontal: 0,
      },
    },
  } as const,
});

const Title = styled(Text, {
  name: "Title",
  fontFamily: "$body",
  fontSize: "$3",
  lineHeight: "$3",
  fontWeight: "$6",
  variants: {
    variant: {
      success: {
        color: "$score-8",
      },
      warning: {
        color: "$warning-500",
      },

      danger: {
        color: "$error-500",
      },
      critical: {
        color: "$error-500",
      },
      info: {
        fontWeight: "$6",
        color: "$darkgreen-500",
      },

      gray: {
        color: "$gray-600",
        fontSize: "$2",
        fontWeight: "$4",
        lineHeight: "$2",
      },
    },
  } as const,
});

const CustomBold = styled(Text, {
  name: "CustomBold",
  display: "inline",
  tag: "span",
  fontFamily: "$body",
  fontSize: "$3",
  lineHeight: "$3",
  fontWeight: "$7",
  variants: {
    state: {
      success: {
        color: "$score-8",
      },
      warning: {
        color: "$warning-500",
      },

      danger: {
        color: "$error-500",
      },
      critical: {
        color: "$error-500",
      },
    },
  } as const,
  defaultVariants: {
    state: "critical",
  },
});

const BlockQuoteTextContent = styled(Text, {
  name: "BlockQuoteTextContent",
  display: "inline",
  fontFamily: "$body",
  tag: "span",
  fontSize: "$3",
  lineHeight: "$3",
  fontWeight: "$4",
  color: "$gray-950",
});

const Icon: React.FC<{ variant: MdIdentifier }> = ({ variant }) => {
  switch (variant) {
    case "success":
      return <CheckDone />;
    case "warning":
      return <Warning />;
    case "critical":
      return <Delete />;
    case "danger":
      return <Delete />;
    default:
      return null;
  }
};

type CustomBoldState = ComponentProps<typeof CustomBold>["state"];

const rulles: RenderRules = {
  custom_emphasis: (node) => {
    const content = collectContent(node).join("");
    return (
      <CustomBold key={node.key} state={node.markup as CustomBoldState}>
        {content}
      </CustomBold>
    );
  },
  custom_block: (n, children) => {
    const node = n as ExtendedASTNode;

    return (
      <BlockQuoteContainer variant={node.markup as MdIdentifier} key={node.key}>
        {!!node.sourceInfo && (
          <XStack
            alignItems="center"
            gap={"$0.5"}
            marginTop={"$1.2"}
            $sm={{ flexDirection: "column", alignItems: "flex-start" }}
          >
            <Icon variant={node.markup as MdIdentifier} />
            <Title variant={node.markup as MdIdentifier}>
              {node.sourceInfo}
            </Title>
          </XStack>
        )}
        <BlockQuoteTextContent>{children}</BlockQuoteTextContent>
      </BlockQuoteContainer>
    );
  },
};

export const RenderMd: React.FC<{ md: string }> = ({ md }) => {
  return (
    <Markdown
      style={{
        body: {
          fontFamily: "Fixel Text",
          fontSize: 16,
          lineHeight: 24,
          fontWeight: 400,
          color: "#111111",
        },
        em: {
          fontFamily: "Fixel Text",
          fontSize: 16,
          lineHeight: 24,
          fontWeight: 400,
          color: "#111111",
        },

        heading1: {
          fontFamily: "Fixel Text",
          fontWeight: "500",
          fontSize: 24,
          lineHeight: 24,
        },
        heading2: {
          fontFamily: "Fixel Text",
          fontWeight: "500",
          fontSize: 20,
          lineHeight: 20,
        },
        heading3: {
          fontFamily: "Fixel Text",
          fontWeight: "500",
          fontSize: 18,
          lineHeight: 18,
          marginBottom: 12,
        },
      }}
      rules={rulles}
      markdownit={markdownInst}
    >
      {md}
    </Markdown>
  );
};
